/* Tableau du pipeline : 5 colonnes, glisser-déposer natif (sans dépendance). */
import React, { useState } from 'react';
import type { AccountState, CrmContact, Stage } from './types';
import { STAGES } from './types';
import { ContactCard } from './ContactCard';
import type { GroupStats } from './participants';

interface Props {
  contacts: CrmContact[];
  /** contactId → état du compte plateforme */
  accounts: Record<string, AccountState>;
  /** Nom du groupe en minuscules → chiffres du groupe */
  groupStats: Record<string, GroupStats>;
  onOpen: (c: CrmContact) => void;
  onMove: (c: CrmContact, target: Stage) => void;
}

export function PipelineBoard({ contacts, accounts, groupStats, onOpen, onMove }: Props) {
  const [dragged, setDragged] = useState<CrmContact | null>(null);
  const [overStage, setOverStage] = useState<Stage | null>(null);

  const handleDragStart = (e: React.DragEvent, c: CrmContact) => {
    e.dataTransfer.setData('text/plain', c.id);
    e.dataTransfer.effectAllowed = 'move';
    setDragged(c);
  };

  const handleDrop = (e: React.DragEvent, stage: Stage) => {
    e.preventDefault();
    setOverStage(null);
    const id = e.dataTransfer.getData('text/plain');
    const contact = dragged ?? contacts.find(c => c.id === id) ?? null;
    setDragged(null);
    if (contact && contact.stage !== stage) onMove(contact, stage);
  };

  return (
    <div className="flex-1 overflow-x-auto overflow-y-hidden">
      <div className="flex gap-3 h-full p-4 min-w-max">
        {STAGES.map(stage => {
          const items = contacts.filter(c => c.stage === stage.key);
          const isOver = overStage === stage.key && dragged?.stage !== stage.key;
          return (
            <div
              key={stage.key}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverStage(stage.key); }}
              onDragLeave={() => setOverStage(prev => (prev === stage.key ? null : prev))}
              onDrop={e => handleDrop(e, stage.key)}
              className={`w-72 flex flex-col rounded-2xl border-t-4 ${stage.accent} transition-colors ${
                isOver ? 'bg-amber-50 ring-2 ring-[#F8BD3D]/60' : 'bg-neutral-50'
              }`}
            >
              <div className="flex items-center justify-between px-3 py-2.5 shrink-0">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${stage.dot}`} />
                  <span className="text-sm font-semibold text-neutral-800">{stage.label}</span>
                </div>
                <span className="text-xs font-semibold text-neutral-500 bg-white border border-neutral-200 rounded-full px-2 py-0.5">
                  {items.length}
                </span>
              </div>
              <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-2">
                {items.length === 0 ? (
                  <div className="text-center text-[11px] text-neutral-400 py-6 border border-dashed border-neutral-200 rounded-xl">
                    Glissez un contact ici
                  </div>
                ) : (
                  items.map(c => (
                    <ContactCard
                      key={c.id}
                      contact={c}
                      account={accounts[c.id]}
                      groupStats={c.groupe ? groupStats[c.groupe.trim().toLowerCase()] : undefined}
                      onOpen={onOpen}
                      onDragStart={handleDragStart}
                      onDragEnd={() => { setDragged(null); setOverStage(null); }}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
